import { ArrowRight, Check } from "lucide-react";
import { motion } from "motion/react";
import { RevealGroup, RevealItem } from "../shared/Reveal";
import { useSectionParallax } from "../../hooks/useSectionParallax";
import { posthog } from "../../lib/posthog";

const SPIN_SLOW = "animate-[spin_7s_linear_infinite]";

interface ProductPanelProps {
  icon: string;
  iconCls?: string;
  name: string;
  category: string;
  summary: string;
  audience: string;
  points: string[];
  accentCls: string;
  borderCls: string;
  primary: { label: string; onClick: () => void; cls: string };
  secondary: { label: string; onClick: () => void };
}

function TrackedButton({ label, onClick, cls, product }: { label: string; onClick: () => void; cls: string; product: string }) {
  return (
    <button
      onClick={() => {
        posthog.capture("cta_clicked", { cta: label, location: "home_product_intro", product });
        onClick();
      }}
      className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-full transition-[transform,border-color] ${cls}`}
    >
      {label} <ArrowRight size={16} />
    </button>
  );
}

function PanelPoints({ points, accentCls }: { points: string[]; accentCls: string }) {
  return (
    <ul className="mt-8 space-y-3">
      {points.map((p) => (
        <li key={p} className="flex items-start gap-3 text-sm text-[#5C5C66] dark:text-[#9A9A9A]">
          <Check size={15} className={`mt-0.5 shrink-0 ${accentCls}`} />
          {p}
        </li>
      ))}
    </ul>
  );
}

function ProductPanel(props: ProductPanelProps) {
  const { icon, iconCls, name, category, summary, audience, points, accentCls, borderCls, primary, secondary } = props;
  return (
    <div className={`h-full flex flex-col rounded-3xl border ${borderCls} bg-white dark:bg-[#0D0D10] p-8 md:p-12`}>
      <div className="flex items-center gap-5">
        <img src={icon} alt="" className={`w-20 h-20 shrink-0 ${SPIN_SLOW} ${iconCls ?? ""}`} />
        <div>
          <p className="text-3xl md:text-4xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white">{name}</p>
          <p className={`text-xs uppercase tracking-widest font-semibold mt-1 ${accentCls}`}>{category}</p>
        </div>
      </div>
      <p className="mt-8 text-lg leading-[1.6] text-[#0A0A0F] dark:text-white">{summary}</p>
      <p className="mt-3 text-sm text-[#5C5C66] dark:text-[#9A9A9A]">{audience}</p>
      <PanelPoints points={points} accentCls={accentCls} />
      <div className="mt-auto pt-10 flex flex-wrap gap-3">
        <TrackedButton {...primary} product={name} />
        <TrackedButton
          {...secondary} product={name}
          cls="border border-[#0A0A0F]/15 dark:border-white/15 text-[#0A0A0F] dark:text-white hover:border-[#0A0A0F]/40 dark:hover:border-white/40"
        />
      </div>
    </div>
  );
}

function SectionIntro() {
  return (
    <div className="text-center mb-12 max-w-2xl mx-auto">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1E4FD8] dark:text-[#4A90D9] mb-3">What we build</p>
      <h2 className="text-3xl md:text-4xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white">
        Two products for complex decisions.
      </h2>
      <p className="mt-4 text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">
        One helps you analyze the data you have. The other simulates the trial you haven&apos;t run yet.
      </p>
    </div>
  );
}

export function ProductIntro({ onIviz, onForesight, onPricing, onDemo }: { onIviz: () => void; onForesight: () => void; onPricing: () => void; onDemo: () => void }) {
  const { ref, fgY } = useSectionParallax();
  return (
    <section ref={ref} className="py-20 bg-[#FAFAFA] dark:bg-[#050505]">
      <div className="max-w-[1800px] mx-auto px-6 lg:px-10">
        <SectionIntro />
        <RevealGroup className="grid lg:grid-cols-2 gap-8">
          <RevealItem className="h-full">
            <motion.div style={{ y: fgY }} className="h-full">
              <ProductPanel
                // The icon's strokes are dark navy; flip lightness (keeping hue) on dark backgrounds.
                icon="/brand/iviz/iviz-icon-transparent.svg" iconCls="dark:invert dark:hue-rotate-180" name="iViz" category="AI analytical workbench"
                summary="Rigorous analysis of any data, with or without code."
                audience="For anyone who works with data. Free plan available."
                points={["Flow: build analyses on a visual, no-code canvas with Mona", "Notebook: Python and R in the cloud with Merlin", "Statistics, regression models, and 12 chart types built in"]}
                accentCls="text-[#1E4FD8] dark:text-[#4A90D9]"
                borderCls="border-[#0A0A0F]/10 dark:border-white/10 hover:border-[#1E4FD8]/40 dark:hover:border-[#4A90D9]/40"
                primary={{ label: "Explore iViz", onClick: onIviz, cls: "text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9] hover:scale-[1.02] active:scale-[0.98]" }}
                secondary={{ label: "See pricing", onClick: onPricing }}
              />
            </motion.div>
          </RevealItem>
          <RevealItem className="h-full">
            <motion.div style={{ y: fgY }} className="h-full">
              <ProductPanel
                icon="/brand/foresight/foresight-mark.svg" name="Foresight" category="Clinical trial simulation"
                summary="Run the trial before the trial runs, on digital twin cohorts."
                audience="For clinical pharmacologists, modelers, and trial designers."
                points={["Digital twin cohorts from your inclusion and exclusion criteria", "Bring existing MATLAB and SBML models, no re-encoding", "Simulate 3+3 escalation and fixed dosing, many times over"]}
                accentCls="text-[#5B21B6] dark:text-[#B9A5FF]"
                borderCls="border-[#0A0A0F]/10 dark:border-white/10 hover:border-[#5B21B6]/40 dark:hover:border-[#7C4DFF]/40"
                primary={{ label: "Discover Foresight", onClick: onForesight, cls: "text-white dark:text-[#050505] bg-[#5B21B6] dark:bg-[#7C4DFF] hover:scale-[1.02] active:scale-[0.98]" }}
                secondary={{ label: "Book a demo", onClick: onDemo }}
              />
            </motion.div>
          </RevealItem>
        </RevealGroup>
      </div>
    </section>
  );
}
